import * as React from "react";
import { Doc } from "@repo/convex/convex/_generated/dataModel";
import { routes } from "../../routing/router.ts";
import { itemKind, parseXPost, XPost } from "./itemKind.ts";
import { domain, itemTitle, videoLength } from "./format.ts";
import { ItemPicture, ItemThumbnail, PlayMark, SiteIcon, XMark } from "./ItemPictures.tsx";
import "./items.css";

// One item in a list, in the shape that suits it ("Watch and read", the iPhone app's ItemRow.swift):
// a video is a card with its picture across the top, since the thumbnail is how you pick a video;
// a post on X is the post itself; everything else is a compact row with a small square picture.
// The Stash, the Archive and the Trash all use it, each with its own buttons on the end
// (`actions` and `menu`), and the Archive and Trash with a line of their own (`detail`).
// Clicking the picture or title opens it in the reader.

type CardItem = Doc<"items">;

interface Props {
  item: CardItem;
  /** "Tucked in 2 Oct", in place of how far through it you are. */
  detail?: React.ReactNode;
  actions?: React.ReactNode;
  menu?: React.ReactNode;
}

export const ItemCard: React.FC<Props> = (props) => {
  const kind = itemKind(props.item.url);
  if (kind.kind == "video") return <VideoCard {...props} />;
  if (kind.kind == "xPost") {
    // A post whose title isn't X's usual one (not fetched yet, or a profile) is shown as it is
    const post = parseXPost(props.item.title, props.item.url);
    if (post) return <PostCard {...props} post={post} />;
  }
  return <ArticleRow {...props} />;
};

/** Drawn as a video's or a post's card, with room for more buttons than an article's row. */
export const isCardShaped = (item: CardItem) => {
  const kind = itemKind(item.url);
  if (kind.kind == "video") return true;
  return kind.kind == "xPost" && parseXPost(item.title, item.url) != null;
};

const readerLink = (item: CardItem) => routes.item({ itemId: item._id }).link;

const VideoCard: React.FC<Props> = ({ item, detail, actions, menu }) => {
  const link = readerLink(item);
  const length = videoLength(item.videoSeconds);
  const title = itemTitle(item);
  return (
    <article className="item-card item-video">
      <a className="item-video-picture" {...link} aria-label={title}>
        <ItemPicture item={item} className="item-video-img" />
        <PlayMark />
        {length && (
          <span className="duration-badge" aria-label={`${length} long`}>
            {length}
          </span>
        )}
      </a>
      <div className="item-video-body">
        <a className="item-title item-title-video" {...link} title={title}>
          {title}
        </a>
        <MetaLine item={item} detail={detail} actions={actions} menu={menu} />
      </div>
    </article>
  );
};

const ArticleRow: React.FC<Props> = ({ item, detail, actions, menu }) => {
  const link = readerLink(item);
  const title = itemTitle(item);
  return (
    <article className="item-card item-row">
      <a className="item-row-thumb" {...link} tabIndex={-1} aria-hidden>
        <ItemThumbnail item={item} />
      </a>
      <div className="item-row-body">
        <a className="item-title" {...link} title={title}>
          {title}
        </a>
        <MetaLine item={item} detail={detail} actions={actions} menu={menu} />
      </div>
    </article>
  );
};

const PostCard: React.FC<Props & { post: XPost }> = ({ item, post, detail, actions, menu }) => {
  const link = readerLink(item);
  return (
    <article className="item-card item-post">
      <a className="item-post-body" {...link}>
        <span className="item-post-head">
          <XMark size={18} />
          <span className="item-post-name">{post.name}</span>
          {post.handle && <span className="item-post-handle">{post.handle}</span>}
        </span>
        <span className="item-post-text">{withMentions(post.text)}</span>
      </a>
      <MetaLine item={item} detail={detail} actions={actions} menu={menu} showsIcon={false} />
    </article>
  );
};

/** "@someone" picked out in wood, the way X does. */
const withMentions = (text: string) =>
  text.split(/(@\w{1,15})/g).map((part, i) =>
    i % 2 == 1 ? (
      <b key={i} className="mention">
        {part}
      </b>
    ) : (
      part
    ),
  );

/** The site's icon and domain, then the list's detail or how far through, then the buttons. */
const MetaLine: React.FC<Props & { showsIcon?: boolean }> = ({
  item,
  detail,
  actions,
  menu,
  showsIcon = true,
}) => {
  const position = item.readingPosition;
  const partRead = detail == null && position != null && position > 0.02 && position < 0.98;
  return (
    <div className="item-meta">
      <span className="item-meta-details">
        {showsIcon && <SiteIcon item={item} />}
        <span className="item-domain">{domain(item.url)}</span>
        {detail != null && (
          <>
            <span aria-hidden>·</span>
            <span className="item-detail">{detail}</span>
          </>
        )}
        {partRead && (
          <>
            <span aria-hidden>·</span>
            <span className="item-progress">{Math.round(position * 100)}% read</span>
            <ReadingBar progress={position} />
          </>
        )}
      </span>
      {(actions || menu) && (
        <span className="item-actions">
          {actions}
          {menu}
        </span>
      )}
    </div>
  );
};

/** How far through an article: a thin wax bar. */
const ReadingBar: React.FC<{ progress: number }> = ({ progress }) => (
  <span className="reading-bar" aria-hidden>
    <span style={{ width: `${Math.min(1, Math.max(0.06, progress)) * 100}%` }} />
  </span>
);

/** A grey card in the shape of the real thing, while the list loads. */
export const ItemCardSkeleton: React.FC<{ video?: boolean }> = ({ video }) =>
  video ? (
    <div className="item-card item-video item-skeleton" aria-hidden>
      <span className="item-video-picture" />
      <div className="item-video-body">
        <span className="skeleton-line" style={{ width: "80%" }} />
        <span className="skeleton-line short" />
      </div>
    </div>
  ) : (
    <div className="item-card item-row item-skeleton" aria-hidden>
      <span className="item-thumb" style={{ width: 64, height: 64 }} />
      <div className="item-row-body">
        <span className="skeleton-line" style={{ width: "85%" }} />
        <span className="skeleton-line short" />
      </div>
    </div>
  );
